import type {
  KeywordDefinition,
  SchemaValidateFunction,
  AnySchemaObject,
} from 'ajv';
import {
  isBlockedProtocol,
  isHrefStructurallyValid,
  getUriMessage,
} from '../../../utils/uri-utils/uri-utils';

// Internal helper to map a reason to an AJV error object.
function toAjvError(
  reason: 'emptyHref' | 'blockedProtocol' | 'invalidHref',
  href: string | undefined,
  parentSchema?: AnySchemaObject,
  ctx?: { instancePath?: string },
) {
  return {
    instancePath: ctx?.instancePath ?? '',
    schemaPath: '#/enforceUriProtocols',
    keyword: 'enforceUriProtocols',
    params: { reason, href },
    parentSchema,
    message: getUriMessage(reason, href) || 'invalid URI',
  };
}

function buildValidateEnforceUriProtocols(
  allowed: string[] | undefined,
): SchemaValidateFunction {
  const validate: SchemaValidateFunction = (
    schema,
    data,
    parentSchema,
    ctx,
  ) => {
    // schema is the boolean flag (true enables enforcement)
    if (schema !== true) return true; // disabled => always pass
    // non-strings handled elsewhere / incompatible schema + no data is valid because required and allowEmpty should be used to enforce presence
    if (typeof data !== 'string' || !data) return true;

    const value = data;
    // Treat empty or whitespace-only exactly as emptyHref (policy: whitespace not a valid href)
    if (/^\s+$/.test(value)) {
      validate.errors = [toAjvError('emptyHref', undefined, parentSchema, ctx)];
      return false;
    }

    // Blocked absolute protocol (javascript:, ftp:, etc.)
    if (isBlockedProtocol(value, allowed)) {
      validate.errors = [
        toAjvError('blockedProtocol', value, parentSchema, ctx),
      ];
      return false;
    }

    // Structural invalidity (malformed absolute URL, bad scheme usage, etc.)
    if (!isHrefStructurallyValid(value, allowed)) {
      validate.errors = [toAjvError('invalidHref', value, parentSchema, ctx)];
      return false;
    }

    validate.errors = undefined;
    return true;
  };
  return validate;
}

// Keyword definition:
// Usage example on a property schema:
// {
//   type: 'string',
//   format: 'uri',
//   enforceUriProtocols: true
// }
// or format: 'uri-reference'. Relative references are allowed unless they
// become absolute with a blocked protocol.
export const buildEnforceUriProtocolsKeyword = (
  allowed?: string[],
): KeywordDefinition => ({
  keyword: 'enforceUriProtocols',
  type: 'string', // applies to string data
  schemaType: 'boolean', // the schema value for the keyword must be a boolean
  errors: true,
  validate: buildValidateEnforceUriProtocols(allowed),
});
